import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectWithSearch } from 'ui';
import addMonths from 'date-fns/addMonths';
import { format } from 'date-fns';
import {InitialMaturityDateRangeValue, StringFilterRangeValue} from '../../types';

type MaturityDateSelect = {
  onChange: (value: StringFilterRangeValue) => void;
  prevValue: StringFilterRangeValue | null;
  maturityRange: InitialMaturityDateRangeValue;
};

export const MaturityDateSelect: FC<MaturityDateSelect> = ({ onChange, prevValue, maturityRange }) => {
  const { t } = useTranslation();

  const startDate = new Date();
  const maxRangeDate = new Date(maturityRange.maximum ?? '')
  const dateFormat: string = "yyyy-MM-dd'T'HH:mm:ss";
  const startDateFormatted: string = format(startDate, dateFormat);

  const datesToPick = [
    {
      label: 'past',
      value: {
        toRange: startDateFormatted,
      },
    },
    {
      label: '1 month',
      value: {
        fromRange: startDateFormatted,
        toRange: format(addMonths(startDate, 1), dateFormat),
      },
    },
    {
      label: '3 months',
      value: {
        fromRange: startDateFormatted,
        toRange: format(addMonths(startDate, 3), dateFormat),
      },
    },
    {
      label: '6 months',
      value: {
        fromRange: startDateFormatted,
        toRange: format(addMonths(startDate, 6), dateFormat),
      },
    },
    {
      label: '1 year',
      value: {
        fromRange: startDateFormatted,
        toRange: format(addMonths(startDate, 12), dateFormat),
      },
    },
    {
      label: '3 years',
      value: {
        fromRange: startDateFormatted,
        toRange: format(addMonths(startDate, 36), dateFormat),
      },
    },
  ];

  if (addMonths(startDate, 36) < maxRangeDate) {
    datesToPick.push({
      label: 'more than 3 years',
      value: {
        fromRange: startDateFormatted,
        toRange: format(maxRangeDate, dateFormat)
      }
    })
  }

  const datesToPickFinder = (valueToRange: string) =>
    datesToPick.find(
      element => element.value?.toRange?.slice(0, 10) === valueToRange?.slice(0, 10),
    )?.value;

  const initialValues = () => {
    if (prevValue === null) {
      return null;
    }
    return prevValue
      ? datesToPickFinder(prevValue.toRange)
      : [datesToPick[0].value, datesToPick[datesToPick.length - 1].value];
  };

  return (
    <SelectWithSearch
      label={t('PORTFOLIO.MATURITY_DATE')}
      options={datesToPick}
      value={initialValues()}
      onChange={onChange}
    />
  );
};
